/**
 * Post-checkout success page.
 *
 * Stripe redirects here after a successful payment. We do NOT grant access
 * based on this redirect — that's done only by the verified webhook. We just
 * wait for that purchase to land (usePurchaseConfirmation).
 */

import { createFileRoute } from '@tanstack/react-router';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { usePurchaseConfirmation } from '../hooks/usePurchaseConfirmation';
import { Button } from '../components/ui/button';

export const Route = createFileRoute('/billing/success')({
  component: SuccessPage,
});

export const FALLBACK_MESSAGE = 'Payment received; credits can take a minute.';
export const FALLBACK_LINK_LABEL = 'Refresh Billing';
export const FALLBACK_LINK_HREF = '/account/billing#activity';

export function PurchaseFallback() {
  return (
    <>
      <h1 className="text-2xl font-bold">{FALLBACK_MESSAGE}</h1>
      <Button asChild className="mt-6" variant="outline">
        <a href={FALLBACK_LINK_HREF}>{FALLBACK_LINK_LABEL}</a>
      </Button>
    </>
  );
}

function SuccessPage() {
  const { status, credits } = usePurchaseConfirmation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md text-center">
        {status === 'pending' && (
          <>
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" />
            <h1 className="mt-6 text-2xl font-bold">Confirming your purchase…</h1>
            <p className="mt-2 text-muted-foreground">
              Stripe has processed your payment. Activating your account — usually takes a few seconds.
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
            <h1 className="mt-6 text-3xl font-bold">You're in</h1>
            <p className="mt-2 text-muted-foreground">
              {credits != null
                ? `${credits} credit${credits === 1 ? '' : 's'} available. Ready to create.`
                : 'Your credits are active. Ready to create.'}
            </p>
            <Button className="mt-6" onClick={() => (window.location.href = '/')}>
              Start creating
            </Button>
            <a href="/account/billing#activity" className="mt-4 block text-sm text-muted-foreground hover:text-foreground">
              View activity
            </a>
          </>
        )}
        {status === 'timeout' && <PurchaseFallback />}
      </div>
    </div>
  );
}
